import React from "react";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../context/AuthContext";
import { acquisitionApi } from "../api/acquisition";
import {
  ACQUISITION_EXPERIENCE_VERSION,
  acquisitionDeviceMetadata,
  acquisitionEventId,
  acquisitionSessionId,
  acquisitionVisitorId,
  saveAcquisitionDraft,
} from "../utils/acquisitionFunnel";
import logo from "../images/Recurso 7.svg";

const BRAND = {
  orange: "#F35A3A",
  orangeDark: "#DF4728",
  dark: "#19242F",
};

function useScrolled(threshold = 8) {
  const [scrolled, setScrolled] = React.useState(false);

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);

  return scrolled;
}

function scrollHomeSection(navigate, id) {
  const scroll = () => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  const route = window.location.hash.slice(1).split("?")[0];
  if (route === "/" || route === "") {
    scroll();
    return;
  }
  navigate("/");
  window.setTimeout(scroll, 100);
}

function getInitials(user) {
  const source = String(user?.name || user?.email || "WP").trim();
  return source.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join("");
}

export default function Navbar() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, logout } = useAuth();
  const scrolled = useScrolled();
  const [open, setOpen] = React.useState(false);
  const [loggingOut, setLoggingOut] = React.useState(false);
  const isHome = location.pathname === "/";
  const navText = isHome ? "#F7F8FA" : BRAND.dark;
  const navMuted = isHome ? "rgba(255,255,255,.5)" : "rgba(25,36,47,.52)";

  const closeAndNavigate = (path) => {
    setOpen(false);
    navigate(path);
  };

  const acquisitionNavigate = React.useCallback((path, entryPoint) => {
    // BLOCK 4 · Navbar acquisition attribution.
    // STATUS: STATIC_PASS · STAGING_PENDING.
    // Only the public home navbar belongs to acquisition. Keeping this boundary
    // prevents normal authenticated navigation elsewhere from polluting the funnel.
    if (isHome && !user) {
      const sessionId = acquisitionSessionId();
      const visitorId = acquisitionVisitorId();
      const audience = loading ? "unknown" : "anonymous";
      saveAcquisitionDraft({ sessionId, visitorId, entryPoint, experienceVersion: ACQUISITION_EXPERIENCE_VERSION });
      acquisitionApi.track({
        action: "acquisition_cta_clicked",
        sessionId,
        visitorId,
        eventId: acquisitionEventId("navcta"),
        metadata: {
          stepKey: "landing",
          stepIndex: 0,
          totalSteps: 9,
          progressPct: 0,
          entryPoint,
          audience,
          experienceVersion: ACQUISITION_EXPERIENCE_VERSION,
          ...acquisitionDeviceMetadata(),
        },
      }).catch(() => null);
    }
    setOpen(false);
    navigate(path);
  }, [isHome, loading, navigate, user]);

  const registerPath = React.useMemo(() => {
    // Preserve UTM/referral parameters across the direct registration shortcut.
    const params = new URLSearchParams(location.search);
    params.set("entry", "navbar-register");
    return `/registro?${params.toString()}`;
  }, [location.search]);

  const handleLogout = async () => {
    try {
      setLoggingOut(true);
      await logout();
      setOpen(false);
      navigate("/", { replace: true });
    } finally {
      setLoggingOut(false);
    }
  };

  const links = [
    { label: "Para tu negocio", action: () => scrollHomeSection(navigate, "como-funciona") },
    { label: "Curso", action: () => scrollHomeSection(navigate, "curso-home") },
    { label: "Módulos", action: () => scrollHomeSection(navigate, "herramientas") },
    { label: "Precios", action: () => scrollHomeSection(navigate, "precios") },
  ];

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: isHome
            ? scrolled ? "rgba(9,14,19,.9)" : "rgba(9,14,19,.66)"
            : scrolled ? "rgba(255,255,255,.94)" : "rgba(255,255,255,.82)",
          color: navText,
          borderBottom: isHome ? "1px solid rgba(255,255,255,.07)" : "1px solid rgba(25,36,47,.08)",
          backdropFilter: "blur(18px)",
          boxShadow: scrolled ? isHome ? "0 12px 35px rgba(0,0,0,.22)" : "0 12px 35px rgba(25,36,47,.08)" : "none",
          transition: "background-color .25s ease, box-shadow .25s ease",
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ minHeight: { xs: 72, md: 78 } }}>
            <Box component={RouterLink} to="/" aria-label="WebProfit - inicio" sx={{ display: "inline-flex", alignItems: "center", gap: 1.25, color: navText, textDecoration: "none" }}>
              <Box component="img" src={logo} alt="WebProfit" sx={{ width: 43, height: 43, objectFit: "contain" }} />
              <Box>
                <Typography sx={{ fontWeight: 950, lineHeight: 1, letterSpacing: "-.035em", fontSize: { xs: "1.25rem", md: "1.45rem" } }}>WebProfit</Typography>
                <Typography sx={{ display: { xs: "none", sm: "block" }, mt: .35, color: navMuted, fontWeight: 750, fontSize: ".68rem", letterSpacing: ".08em", textTransform: "uppercase" }}>Tu negocio online</Typography>
              </Box>
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            {isDesktop ? (
              <Stack direction="row" alignItems="center" spacing={.45}>
                {links.map((item) => (
                  <Button key={item.label} onClick={item.action} sx={{ px: 1.35, color: navText, borderRadius: 999, textTransform: "none", fontWeight: 800, "&:hover": { bgcolor: isHome ? "rgba(255,255,255,.06)" : "rgba(25,36,47,.045)" } }}>{item.label}</Button>
                ))}

                {!loading && user ? (
                  <Stack direction="row" alignItems="center" spacing={1.1} sx={{ ml: 1.2 }}>
                    <Stack direction="row" alignItems="center" spacing={.9}>
                      <Avatar sx={{ width: 36, height: 36, bgcolor: "rgba(243,90,58,.14)", color: isHome ? "#FFB19A" : "#C83E21", fontWeight: 950, fontSize: ".82rem" }}>{getInitials(user)}</Avatar>
                      <Box sx={{ maxWidth: 150 }}>
                        <Typography noWrap sx={{ color: navText, fontWeight: 900, fontSize: ".82rem", lineHeight: 1.1 }}>{user.name || "Mi cuenta"}</Typography>
                        <Typography noWrap sx={{ mt: .25, color: navMuted, fontWeight: 700, fontSize: ".68rem" }}>Sesión activa</Typography>
                      </Box>
                    </Stack>
                    <Button onClick={() => navigate("/dashboard")} variant="contained" startIcon={<DashboardRoundedIcon />} sx={{ minHeight: 44, px: 2.2, borderRadius: 999, bgcolor: BRAND.orange, textTransform: "none", fontWeight: 900, boxShadow: "0 10px 24px rgba(243,90,58,.24)", "&:hover": { bgcolor: BRAND.orangeDark, transform: "translateY(-1px)" } }}>Ir a mi panel</Button>
                  </Stack>
                ) : (
                  <>
                    <Button onClick={() => acquisitionNavigate("/login", "navbar-login")} startIcon={<LoginRoundedIcon />} disabled={loading} sx={{ ml: .8, color: navText, borderRadius: 999, textTransform: "none", fontWeight: 850 }}>Ingresar</Button>
                    <Button onClick={() => acquisitionNavigate(registerPath, "navbar-register")} variant="contained" endIcon={<ArrowForwardRoundedIcon />} disabled={loading} sx={{ ml: .8, minHeight: 44, px: 2.4, borderRadius: 999, bgcolor: BRAND.orange, textTransform: "none", fontWeight: 900, boxShadow: "0 10px 24px rgba(243,90,58,.24)", "&:hover": { bgcolor: BRAND.orangeDark, transform: "translateY(-1px)" } }}>Crear página gratis</Button>
                  </>
                )}
              </Stack>
            ) : (
              <IconButton aria-label="Abrir menú" onClick={() => setOpen(true)} sx={{ color: navText, border: isHome ? "1px solid rgba(255,255,255,.12)" : "1px solid rgba(25,36,47,.11)", borderRadius: 2.5 }}><MenuRoundedIcon /></IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer anchor="right" open={open} onClose={() => setOpen(false)} PaperProps={{ sx: { width: "min(88vw, 390px)", bgcolor: "#0E151D", color: "#F7F8FA", p: 2.5 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1.1}><Box component="img" src={logo} alt="WebProfit" sx={{ width: 38, height: 38 }} /><Typography sx={{ fontWeight: 950, fontSize: "1.3rem" }}>WebProfit</Typography></Stack>
          <IconButton aria-label="Cerrar menú" onClick={() => setOpen(false)} sx={{ color: "#fff" }}><CloseRoundedIcon /></IconButton>
        </Stack>

        <Divider sx={{ my: 2.5, borderColor: "rgba(255,255,255,.08)" }} />

        <Stack spacing={.7}>
          <Button onClick={() => closeAndNavigate("/")} sx={{ justifyContent: "flex-start", color: "#F7F8FA", textTransform: "none", fontWeight: 850, py: 1.25 }}>Inicio</Button>
          {links.map((item) => <Button key={item.label} onClick={() => { setOpen(false); item.action(); }} sx={{ justifyContent: "flex-start", color: "#F7F8FA", textTransform: "none", fontWeight: 850, py: 1.25 }}>{item.label}</Button>)}
        </Stack>

        {!loading && user ? (
          <Box sx={{ mt: 3, p: 2.2, borderRadius: 4, bgcolor: "rgba(54,193,135,.08)", border: "1px solid rgba(54,193,135,.14)" }}>
            <Stack direction="row" spacing={1.2} alignItems="center"><Avatar sx={{ width: 42, height: 42, bgcolor: "#2A9D67", fontWeight: 950, fontSize: ".88rem" }}>{getInitials(user)}</Avatar><Box sx={{ minWidth: 0 }}><Typography noWrap sx={{ color: "#fff", fontWeight: 900 }}>{user.name || "Mi cuenta"}</Typography><Typography noWrap sx={{ color: "rgba(255,255,255,.55)", fontSize: ".82rem" }}>{user.email || "Sesión activa"}</Typography></Box></Stack>
            <Stack direction="row" spacing={.8} alignItems="center" sx={{ mt: 1.5 }}><CheckCircleRoundedIcon sx={{ color: "#36C187", fontSize: 19 }} /><Typography sx={{ color: "#8AE3BB", fontWeight: 850, fontSize: ".86rem" }}>Tu sesión continúa activa.</Typography></Stack>
          </Box>
        ) : (
          <Box sx={{ mt: 3, p: 2.2, borderRadius: 4, bgcolor: "rgba(243,90,58,.08)", border: "1px solid rgba(243,90,58,.14)" }}>
            <Stack direction="row" spacing={1} alignItems="center"><CheckCircleRoundedIcon sx={{ color: BRAND.orange }} /><Typography sx={{ color: "#FFB19A", fontWeight: 900 }}>Empezá gratis</Typography></Stack>
            <Typography sx={{ mt: 1, color: "rgba(255,255,255,.6)", fontSize: ".9rem", lineHeight: 1.55 }}>Creá tu cuenta y armá tu primera página web. Después sumás sólo las herramientas que realmente necesitás.</Typography>
          </Box>
        )}

        <Stack spacing={1.2} sx={{ mt: "auto", pt: 4 }}>
          {!loading && user ? (
            <>
              <Button onClick={() => closeAndNavigate("/dashboard")} variant="contained" startIcon={<DashboardRoundedIcon />} sx={{ minHeight: 52, borderRadius: 999, bgcolor: BRAND.orange, textTransform: "none", fontWeight: 900, "&:hover": { bgcolor: BRAND.orangeDark } }}>Continuar en mi panel</Button>
              <Button onClick={handleLogout} disabled={loggingOut} startIcon={<LogoutRoundedIcon />} sx={{ minHeight: 48, borderRadius: 999, color: "rgba(255,255,255,.62)", textTransform: "none", fontWeight: 800 }}>{loggingOut ? "Cerrando sesión..." : "Cerrar sesión"}</Button>
            </>
          ) : (
            <>
              <Button onClick={() => acquisitionNavigate(registerPath, "navbar-register")} variant="contained" endIcon={<ArrowForwardRoundedIcon />} disabled={loading} sx={{ minHeight: 52, borderRadius: 999, bgcolor: BRAND.orange, textTransform: "none", fontWeight: 900, "&:hover": { bgcolor: BRAND.orangeDark } }}>Crear mi página gratis</Button>
              <Button onClick={() => acquisitionNavigate("/login", "navbar-login")} variant="outlined" startIcon={<LoginRoundedIcon />} disabled={loading} sx={{ minHeight: 50, borderRadius: 999, color: "#F7F8FA", borderColor: "rgba(255,255,255,.18)", textTransform: "none", fontWeight: 850 }}>Ya tengo una cuenta</Button>
            </>
          )}
        </Stack>
      </Drawer>
    </>
  );
}
